import { DeviceInfo } from '../common/enums'
import { DeviceStatus } from './deviceStatus'

/**
 * Where each device sits in the Dashboard's fleet health bar.
 *
 * The status API only looks back 30 days (DEVICE_STATUS_LOOKBACK_DAYS), so a
 * device it calls `pending` has sent nothing in that window: it may have gone
 * quiet a month ago, years ago, or never have transmitted at all. What the API
 * cannot tell apart, the bar does not pretend to - it says "no reading in 30+
 * days".
 *
 * The one split that is possible client-side is unshipped stock. A device with
 * no location has never been assigned to an account, and in production almost
 * all of those have never transmitted. Counting them inside the bar would show
 * about 290 "broken" devices that were never deployed, so for administrators
 * they sit beside it instead. Customers never see unassigned devices.
 */
export type FleetBucket = 'reporting' | 'stopped' | 'silent' | 'unassigned'

export interface FleetHealth {
  reporting: DeviceInfo[]
  stopped: DeviceInfo[]
  silent: DeviceInfo[]
  unassigned: DeviceInfo[]
}

export function fleetBucket(
  device: DeviceInfo,
  status: DeviceStatus | undefined,
  isAdmin: boolean
): FleetBucket {
  if (status === 'online') return 'reporting'
  if (status === 'offline') return 'stopped'
  if (isAdmin && !device.locationId) return 'unassigned'
  return 'silent'
}

export function fleetHealth(
  devices: DeviceInfo[],
  statuses: Record<string, { status: DeviceStatus } | undefined>,
  isAdmin: boolean
): FleetHealth {
  const health: FleetHealth = { reporting: [], stopped: [], silent: [], unassigned: [] }
  for (const device of devices) {
    health[fleetBucket(device, statuses[device.sigfoxId]?.status, isAdmin)].push(device)
  }
  return health
}

/**
 * Devices grouped by account, largest group first, for the "No reading in
 * 30+ days" panel. These devices have no last-reading time to sort by, so the
 * account is the only useful way to break up a list of a couple of hundred.
 */
export function groupByAccount(devices: DeviceInfo[]): { account: string; devices: DeviceInfo[] }[] {
  const groups = new Map<string, DeviceInfo[]>()
  for (const device of devices) {
    const account = device.accountName || 'No account name'
    const list = groups.get(account)
    if (list) list.push(device)
    else groups.set(account, [device])
  }
  return Array.from(groups, ([account, list]) => ({
    account,
    devices: [...list].sort((a, b) => (a.deviceName || a.sigfoxId).localeCompare(b.deviceName || b.sigfoxId)),
  })).sort((a, b) => b.devices.length - a.devices.length || a.account.localeCompare(b.account))
}
